import { useEffect, useState } from "react";
import { GROUPS, type GroupCode } from "@riseafrica/scoring";
import { CONFIG } from "../config";
import { DEMO_FEED } from "../content/copy";

/**
 * Bandeau d'activité (ticket T14).
 *
 * Alimenté par les derniers diagnostics réellement enregistrés. **Si la source
 * est vide, le bandeau disparaît** — il n'affiche jamais d'exemples pour
 * meubler, ce que la règle non négociable n° 6 interdit. Le mode `demoData`
 * reste possible en développement, et affiche alors la mention « données
 * d'exemple » : un faux signal signalé n'en est plus un.
 *
 * Ce qui est annoncé : un groupe professionnel et une tranche d'indice. Jamais
 * un métier précis avec un score exact — dans un groupe peu fourni, ce couple
 * suffirait à reconnaître quelqu'un.
 */

interface LigneActivite {
  groupe: GroupCode;
  niveau: number;
  tranche: string;
}

export function Ticker() {
  const [index, setIndex] = useState(0);
  const [lignes, setLignes] = useState<string[]>([]);

  useEffect(() => {
    if (CONFIG.demoData) {
      setLignes([...DEMO_FEED]);
      return;
    }
    let vivant = true;
    fetch(CONFIG.activiteEndpoint)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((corps: { lignes?: LigneActivite[] }) => {
        if (!vivant) return;
        setLignes(
          (corps.lignes ?? [])
            .filter((ligne) => ligne.groupe in GROUPS)
            .map(
              (ligne) =>
                `${GROUPS[ligne.groupe].label} — indice ${ligne.tranche}`,
            ),
        );
      })
      .catch(() => {
        // Une source injoignable est traitée comme une source vide : pas de
        // bandeau plutôt qu'un bandeau inventé.
        if (vivant) setLignes([]);
      });
    return () => {
      vivant = false;
    };
  }, []);

  useEffect(() => {
    if (lignes.length < 2) return;
    const timer = window.setInterval(() => setIndex((i) => i + 1), 3600);
    return () => window.clearInterval(timer);
  }, [lignes.length]);

  if (lignes.length === 0) return null;

  return (
    <div className="ticker">
      <div className="ticker-in">
        <span className="dot" />
        <span className="roll">{lignes[index % lignes.length]}</span>
        {CONFIG.demoData ? (
          <span className="tag">données d'exemple</span>
        ) : (
          <span className="tag">diagnostics récents</span>
        )}
      </div>
    </div>
  );
}
